import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  PATHS,
  legacyPageToPath,
  portalPathForRole,
  studentTabPath,
  instructorTabPath,
  instructorStudentPath,
  instructorTabFromPathname,
  studentTabFromPathname,
} from './paths';

/**
 * Uygulama navigasyonu — React Router sarmalayıcısı.
 * Bileşenler doğrudan useNavigate yerine bu hook'u kullanır (Dependency Inversion).
 */
export function useAppNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  const goToLanding = useCallback(() => navigate(PATHS.LANDING), [navigate]);
  const goToLogin = useCallback(() => navigate(PATHS.LOGIN), [navigate]);
  const goToKvkk = useCallback(() => navigate(PATHS.KVKK), [navigate]);

  const goToPortal = useCallback(
    (role) => navigate(portalPathForRole(role), { replace: true }),
    [navigate]
  );

  const goToStudentTab = useCallback(
    (tab) => navigate(studentTabPath(tab)),
    [navigate]
  );

  const goToInstructorTab = useCallback(
    (tab) => navigate(instructorTabPath(tab)),
    [navigate]
  );

  const goToInstructorStudent = useCallback(
    (studentId) => navigate(instructorStudentPath(studentId)),
    [navigate]
  );

  const goToLandingUnlessAuthScreen = useCallback(() => {
    const path = location.pathname;
    if (path !== PATHS.LANDING && path !== PATHS.LOGIN && path !== PATHS.KVKK) {
      navigate(PATHS.LANDING);
    }
  }, [location.pathname, navigate]);

  /** @deprecated currentPage state yerine — geriye dönük uyumluluk */
  const setCurrentPage = useCallback(
    (page) => {
      if (typeof page === 'function') {
        console.warn('setCurrentPage fonksiyon güncellemesi desteklenmiyor; landing kullanılıyor.');
        navigate(PATHS.LANDING);
        return;
      }
      navigate(legacyPageToPath(page));
    },
    [navigate]
  );

  /** @deprecated activeTab state yerine — geriye dönük uyumluluk */
  const setActiveTab = useCallback(
    (tab) => navigate(instructorTabPath(tab)),
    [navigate]
  );

  const activeInstructorTab = useMemo(
    () => instructorTabFromPathname(location.pathname),
    [location.pathname]
  );

  const activeStudentTab = useMemo(
    () => studentTabFromPathname(location.pathname),
    [location.pathname]
  );

  return useMemo(
    () => ({
      navigate,
      location,
      goToLanding,
      goToLogin,
      goToKvkk,
      goToPortal,
      goToStudentTab,
      goToInstructorTab,
      goToInstructorStudent,
      goToLandingUnlessAuthScreen,
      setCurrentPage,
      setActiveTab,
      activeInstructorTab,
      activeStudentTab,
    }),
    [
      navigate,
      location,
      goToLanding,
      goToLogin,
      goToKvkk,
      goToPortal,
      goToStudentTab,
      goToInstructorTab,
      goToInstructorStudent,
      goToLandingUnlessAuthScreen,
      setCurrentPage,
      setActiveTab,
      activeInstructorTab,
      activeStudentTab,
    ]
  );
}
